import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { CheckCircle2, ShieldCheck, Mail, ArrowLeft, Search, Package, FileText, Truck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";

const ProductNotFound = () => {
  const { language, t, getLocalizedPath } = useLanguage();
  
  const notFoundTranslations = {
    en: {
      title: "Product Not Listed in the Online Catalog",
      metaTitle: "Product Request | KM KIT - Industrial Components",
      metaDescription: "Request industrial components, spare parts, or equipment not listed in our catalog. KM KIT sources custom solutions from OEM and European manufacturers.",
      explanation: "This product is not currently displayed in our online catalog.",
      subtext: "KM KIT supplies a wide range of industrial equipment, assemblies, and components, many of which are sourced on request and not publicly listed.",
      helpText: "If you are looking for a specific item, model, or part number, our team can assist.",
      cta: "Contact Us for This Product",
      ctaSubtext: "Share what you are looking for — even partial information is enough.",
      whyKmkit: {
        title: "Why KM KIT",
        points: [
          "Custom and project-based sourcing",
          "Access to OEM and European manufacturers",
          "Support for both equipment and components",
          "Direct communication with specialists",
        ],
      },
      features: {
        sourcing: "Industrial Sourcing",
        sourcingDesc: "We source from verified suppliers worldwide",
        delivery: "Worldwide Delivery",
        deliveryDesc: "Logistics solutions for any destination",
        docs: "Full Documentation",
        docsDesc: "Certificates and technical specs included",
      },
      reassurance: "If a solution exists — we will help you find it.",
      backToCatalog: "Back to Catalog",
    },
    de: {
      title: "Produkt nicht im Online-Katalog gelistet",
      metaTitle: "Produktanfrage | KM KIT - Industriekomponenten",
      metaDescription: "Fordern Sie Industriekomponenten, Ersatzteile oder Ausrüstung an, die nicht in unserem Katalog aufgeführt sind. KM KIT beschafft individuelle Lösungen von OEM und europäischen Herstellern.",
      explanation: "Dieses Produkt wird derzeit nicht in unserem Online-Katalog angezeigt.",
      subtext: "KM KIT liefert eine breite Palette an Industrieausrüstung, Baugruppen und Komponenten, von denen viele auf Anfrage beschafft und nicht öffentlich gelistet werden.",
      helpText: "Wenn Sie nach einem bestimmten Artikel, Modell oder einer Teilenummer suchen, kann unser Team Ihnen helfen.",
      cta: "Kontaktieren Sie uns für dieses Produkt",
      ctaSubtext: "Teilen Sie uns mit, wonach Sie suchen — auch Teilinformationen sind ausreichend.",
      whyKmkit: {
        title: "Warum KM KIT",
        points: [
          "Individuelle und projektbezogene Beschaffung",
          "Zugang zu OEM und europäischen Herstellern",
          "Unterstützung für Ausrüstung und Komponenten",
          "Direkte Kommunikation mit Spezialisten",
        ],
      },
      features: {
        sourcing: "Industriebeschaffung",
        sourcingDesc: "Wir beschaffen von verifizierten Lieferanten weltweit",
        delivery: "Weltweite Lieferung",
        deliveryDesc: "Logistiklösungen für jedes Ziel",
        docs: "Vollständige Dokumentation",
        docsDesc: "Zertifikate und technische Spezifikationen inklusive",
      },
      reassurance: "Wenn eine Lösung existiert — helfen wir Ihnen, sie zu finden.",
      backToCatalog: "Zurück zum Katalog",
    },
    es: {
      title: "Producto no listado en el catálogo en línea",
      metaTitle: "Solicitud de Producto | KM KIT - Componentes Industriales",
      metaDescription: "Solicite componentes industriales, repuestos o equipos no listados en nuestro catálogo. KM KIT obtiene soluciones personalizadas de fabricantes OEM y europeos.",
      explanation: "Este producto no se muestra actualmente en nuestro catálogo en línea.",
      subtext: "KM KIT suministra una amplia gama de equipos industriales, ensamblajes y componentes, muchos de los cuales se obtienen bajo pedido y no están listados públicamente.",
      helpText: "Si está buscando un artículo, modelo o número de pieza específico, nuestro equipo puede ayudarle.",
      cta: "Contáctenos por este producto",
      ctaSubtext: "Comparta lo que está buscando — información parcial es suficiente.",
      whyKmkit: {
        title: "Por Qué KM KIT",
        points: [
          "Abastecimiento personalizado y por proyecto",
          "Acceso a fabricantes OEM y europeos",
          "Soporte para equipos y componentes",
          "Comunicación directa con especialistas",
        ],
      },
      features: {
        sourcing: "Abastecimiento Industrial",
        sourcingDesc: "Obtenemos de proveedores verificados en todo el mundo",
        delivery: "Entrega Mundial",
        deliveryDesc: "Soluciones logísticas para cualquier destino",
        docs: "Documentación Completa",
        docsDesc: "Certificados y especificaciones técnicas incluidas",
      },
      reassurance: "Si existe una solución — le ayudaremos a encontrarla.",
      backToCatalog: "Volver al Catálogo",
    },
  };

  const content = notFoundTranslations[language] || notFoundTranslations.en;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>{content.metaTitle}</title>
        <meta name="description" content={content.metaDescription} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Header />
      <ScrollToTop />
      
      <main className="flex-1 py-8 lg:py-12">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to={getLocalizedPath("/products")} className="hover:text-foreground transition-colors">
              {t('product.catalog')}
            </Link>
            <span>/</span>
            <span className="text-foreground">{content.title}</span>
          </nav>

          <Link 
            to={getLocalizedPath("/products")} 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {content.backToCatalog}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Main Content */}
            <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
              {/* Left Column - Main Info */}
              <div className="lg:col-span-3 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-muted rounded-xl">
                    <Search className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-3">
                      {content.title}
                    </h1>
                    <p className="text-muted-foreground leading-relaxed">
                      {content.explanation}
                    </p>
                  </div>
                </div>

                <Card className="border-border">
                  <CardContent className="p-6">
                    <p className="text-foreground leading-relaxed mb-4">
                      {content.subtext}
                    </p>
                    <p className="text-muted-foreground">
                      {content.helpText}
                    </p>
                  </CardContent>
                </Card>

                {/* Features Grid */}
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
                    <Package className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground text-sm">{content.features.sourcing}</p>
                      <p className="text-xs text-muted-foreground mt-1">{content.features.sourcingDesc}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
                    <Truck className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground text-sm">{content.features.delivery}</p>
                      <p className="text-xs text-muted-foreground mt-1">{content.features.deliveryDesc}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
                    <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground text-sm">{content.features.docs}</p>
                      <p className="text-xs text-muted-foreground mt-1">{content.features.docsDesc}</p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="pt-4">
                  <Button asChild size="lg" className="bg-accent hover:bg-accent/90 w-full sm:w-auto">
                    <Link to={getLocalizedPath("contacts")}>
                      <Mail className="w-4 h-4 mr-2" />
                      {content.cta}
                    </Link>
                  </Button>
                  <p className="text-sm text-muted-foreground mt-3">{content.ctaSubtext}</p>
                </div>
              </div>

              {/* Right Column - Why KM KIT */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="border-primary/20 bg-primary/5">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">{content.whyKmkit.title}</h3>
                    <ul className="space-y-3">
                      {content.whyKmkit.points.map((point, index) => (
                        <li key={index} className="flex items-start text-sm text-muted-foreground">
                          <CheckCircle2 className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 text-center border border-primary/20">
                  <ShieldCheck className="h-10 w-10 text-primary mx-auto mb-4" />
                  <p className="text-lg font-medium text-foreground">{content.reassurance}</p>
                </div>

                {/* Contact Card */}
                <Card className="border-border">
                  <CardContent className="p-6 text-center">
                    <p className="font-semibold text-foreground mb-2">KM KIT mbH</p>
                    <p className="text-sm text-muted-foreground mb-1">Aschmattstraße 8, 76532 Baden-Baden</p>
                    <p className="text-sm text-muted-foreground">Germany</p>
                    <a 
                      href="mailto:info@kmkit.com" 
                      className="inline-flex items-center gap-2 text-primary hover:underline mt-3 text-sm"
                    >
                      <Mail className="w-4 h-4" />
                      info@kmkit.com
                    </a>
                  </CardContent>
                </Card>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductNotFound;